/**
 * Fact-based page text in every site language (docs: seo/strategy.md §Quality).
 *
 * Every sentence is built only from facts the page actually has (year, size, museum, movement,
 * subjects…). A fact that is missing drops its sentence instead of being invented, so two pages
 * never share the same body unless they share the same facts.
 */
import type { Locale } from "./locales";

export type ArtworkFacts = {
  title: string;
  artist: string;
  year?: string | null;
  heightCm?: number | null;
  widthCm?: number | null;
  museum?: string | null;
  movement?: string | null;
  genre?: string | null;
  subjects?: string[];
};

export type ArtistFacts = {
  name: string;
  years?: string | null;
  nationality?: string | null;
  count: number;
  movements?: string[];
  topWorks?: string[];
};

const list = (items: string[], locale: Locale): string =>
  new Intl.ListFormat(locale, { style: "long", type: "conjunction" }).format(items);

const size = (f: ArtworkFacts) => (f.heightCm && f.widthCm ? `${f.heightCm} × ${f.widthCm} cm` : null);

type Parts = {
  intro: (f: ArtworkFacts) => string;
  size: (s: string) => string;
  museum: (m: string) => string;
  movement: (m: string) => string;
  genre: (g: string) => string;
  subjects: (s: string) => string;
  offer: (f: ArtworkFacts) => string;
};

const ARTWORK: Record<Locale, Parts> = {
  en: {
    intro: (f) => `${f.title} is a painting by ${f.artist}${f.year ? `, created in ${f.year}` : ""}.`,
    size: (s) => `The original measures ${s}.`,
    museum: (m) => `It is held in the collection of ${m}.`,
    movement: (m) => `The work belongs to ${m}.`,
    genre: (g) => `Genre: ${g}.`,
    subjects: (s) => `It depicts ${s}.`,
    offer: (f) => `We reproduce ${f.title} by hand in oil on canvas, in the size of your choice, faithful to ${f.artist}'s colours and brushwork.`,
  },
  es: {
    intro: (f) => `${f.title} es una pintura de ${f.artist}${f.year ? `, realizada en ${f.year}` : ""}.`,
    size: (s) => `El original mide ${s}.`,
    museum: (m) => `Forma parte de la colección de ${m}.`,
    movement: (m) => `La obra pertenece a ${m}.`,
    genre: (g) => `Género: ${g}.`,
    subjects: (s) => `Representa ${s}.`,
    offer: (f) => `Reproducimos ${f.title} a mano, al óleo sobre lienzo y en el tamaño que elijas, fiel a los colores y la pincelada de ${f.artist}.`,
  },
  fr: {
    intro: (f) => `${f.title} est une peinture de ${f.artist}${f.year ? `, réalisée en ${f.year}` : ""}.`,
    size: (s) => `L'original mesure ${s}.`,
    museum: (m) => `Elle est conservée dans la collection de ${m}.`,
    movement: (m) => `L'œuvre appartient au mouvement ${m}.`,
    genre: (g) => `Genre : ${g}.`,
    subjects: (s) => `Elle représente ${s}.`,
    offer: (f) => `Nous reproduisons ${f.title} à la main, à l'huile sur toile et au format de votre choix, fidèle aux couleurs et à la touche de ${f.artist}.`,
  },
  de: {
    intro: (f) => `„${f.title}“ ist ein Gemälde von ${f.artist}${f.year ? ` aus dem Jahr ${f.year}` : ""}.`,
    size: (s) => `Das Original misst ${s}.`,
    museum: (m) => `Es befindet sich in der Sammlung von ${m}.`,
    movement: (m) => `Das Werk gehört zur Stilrichtung ${m}.`,
    genre: (g) => `Gattung: ${g}.`,
    subjects: (s) => `Dargestellt sind ${s}.`,
    offer: (f) => `Wir malen „${f.title}“ von Hand in Öl auf Leinwand nach, in der Größe Ihrer Wahl und getreu den Farben und dem Pinselstrich von ${f.artist}.`,
  },
  it: {
    intro: (f) => `${f.title} è un dipinto di ${f.artist}${f.year ? `, realizzato nel ${f.year}` : ""}.`,
    size: (s) => `L'originale misura ${s}.`,
    museum: (m) => `Fa parte della collezione di ${m}.`,
    movement: (m) => `L'opera appartiene al movimento ${m}.`,
    genre: (g) => `Genere: ${g}.`,
    subjects: (s) => `Raffigura ${s}.`,
    offer: (f) => `Riproduciamo ${f.title} a mano, a olio su tela e nel formato che preferisci, fedeli ai colori e alla pennellata di ${f.artist}.`,
  },
  ja: {
    intro: (f) => `『${f.title}』は${f.artist}${f.year ? `が${f.year}年に` : "が"}描いた絵画です。`,
    size: (s) => `原画のサイズは${s}です。`,
    museum: (m) => `${m}に所蔵されています。`,
    movement: (m) => `${m}に属する作品です。`,
    genre: (g) => `ジャンル：${g}。`,
    subjects: (s) => `${s}が描かれています。`,
    offer: (f) => `『${f.title}』を、${f.artist}の色彩と筆致に忠実に、お好みのサイズで油絵具を使いキャンバスに手描きで再現します。`,
  },
  vi: {
    intro: (f) => `${f.title} là bức tranh của ${f.artist}${f.year ? `, vẽ năm ${f.year}` : ""}.`,
    size: (s) => `Bản gốc có kích thước ${s}.`,
    museum: (m) => `Tranh thuộc bộ sưu tập của ${m}.`,
    movement: (m) => `Tác phẩm thuộc trường phái ${m}.`,
    genre: (g) => `Thể loại: ${g}.`,
    subjects: (s) => `Tranh thể hiện ${s}.`,
    offer: (f) => `Chúng tôi vẽ tay lại bức ${f.title} bằng sơn dầu trên toan, theo kích thước bạn chọn, trung thành với màu sắc và nét cọ của ${f.artist}.`,
  },
};

export function describeArtwork(locale: Locale, f: ArtworkFacts): string {
  const p = ARTWORK[locale] ?? ARTWORK.en;
  const s = size(f);
  return [
    p.intro(f),
    s && p.size(s),
    f.museum && p.museum(f.museum),
    f.movement && p.movement(f.movement),
    f.genre && p.genre(f.genre),
    f.subjects?.length ? p.subjects(list(f.subjects.slice(0, 4), locale)) : null,
    p.offer(f),
  ].filter(Boolean).join(" ");
}

/** Short version for <meta name="description"> (≤ ~160 chars, stops at a sentence). */
export function metaDescription(text: string, max = 160): string {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("。"));
  return end > 60 ? cut.slice(0, end + 1) : cut.slice(0, max - 1).trimEnd() + "…";
}

const ARTIST: Record<Locale, (f: ArtistFacts) => string[]> = {
  en: (f) => [
    `${f.name}${f.years ? ` (${f.years})` : ""} was ${f.nationality ? `a painter from ${f.nationality}` : "a painter"}.`,
    f.movements?.length ? `Associated with ${list(f.movements, "en")}.` : "",
    f.topWorks?.length ? `Well-known works include ${list(f.topWorks, "en")}.` : "",
    `Browse ${f.count} paintings by ${f.name}, each available as a hand-painted oil reproduction.`,
  ],
  es: (f) => [
    `${f.name}${f.years ? ` (${f.years})` : ""} fue ${f.nationality ? `un pintor de ${f.nationality}` : "un pintor"}.`,
    f.movements?.length ? `Asociado a ${list(f.movements, "es")}.` : "",
    f.topWorks?.length ? `Entre sus obras conocidas: ${list(f.topWorks, "es")}.` : "",
    `Explora ${f.count} pinturas de ${f.name}, todas disponibles como reproducción al óleo pintada a mano.`,
  ],
  fr: (f) => [
    `${f.name}${f.years ? ` (${f.years})` : ""} est ${f.nationality ? `un peintre originaire de ${f.nationality}` : "un peintre"}.`,
    f.movements?.length ? `Associé à ${list(f.movements, "fr")}.` : "",
    f.topWorks?.length ? `Parmi ses œuvres connues : ${list(f.topWorks, "fr")}.` : "",
    `Découvrez ${f.count} tableaux de ${f.name}, tous disponibles en reproduction à l'huile peinte à la main.`,
  ],
  de: (f) => [
    `${f.name}${f.years ? ` (${f.years})` : ""} war ${f.nationality ? `ein Maler aus ${f.nationality}` : "ein Maler"}.`,
    f.movements?.length ? `Zugeordnet: ${list(f.movements, "de")}.` : "",
    f.topWorks?.length ? `Bekannte Werke: ${list(f.topWorks, "de")}.` : "",
    `Entdecken Sie ${f.count} Gemälde von ${f.name}, jedes als handgemalte Öl-Reproduktion erhältlich.`,
  ],
  it: (f) => [
    `${f.name}${f.years ? ` (${f.years})` : ""} è stato ${f.nationality ? `un pittore di origine ${f.nationality}` : "un pittore"}.`,
    f.movements?.length ? `Legato a ${list(f.movements, "it")}.` : "",
    f.topWorks?.length ? `Tra le opere più note: ${list(f.topWorks, "it")}.` : "",
    `Scopri ${f.count} dipinti di ${f.name}, ciascuno disponibile come riproduzione a olio dipinta a mano.`,
  ],
  ja: (f) => [
    `${f.name}${f.years ? `（${f.years}）` : ""}は${f.nationality ? `${f.nationality}の` : ""}画家です。`,
    f.movements?.length ? `${list(f.movements, "ja")}に関わりました。` : "",
    f.topWorks?.length ? `代表作に${list(f.topWorks, "ja")}などがあります。` : "",
    `${f.name}の作品${f.count}点を、手描きの油絵複製としてご注文いただけます。`,
  ],
  vi: (f) => [
    `${f.name}${f.years ? ` (${f.years})` : ""} là ${f.nationality ? `họa sĩ người ${f.nationality}` : "họa sĩ"}.`,
    f.movements?.length ? `Gắn với ${list(f.movements, "vi")}.` : "",
    f.topWorks?.length ? `Các tác phẩm nổi bật: ${list(f.topWorks, "vi")}.` : "",
    `Xem ${f.count} bức tranh của ${f.name}, bức nào cũng có thể đặt vẽ tay bằng sơn dầu.`,
  ],
};

export function describeArtist(locale: Locale, f: ArtistFacts): string {
  return (ARTIST[locale] ?? ARTIST.en)(f).filter(Boolean).join(" ");
}

/** Taxonomy URL segments, one per browse axis. */
export const TAXON_KINDS = ["museums", "movements", "subjects", "genres"] as const;
export type TaxonKindPath = (typeof TAXON_KINDS)[number];
export const TAXON_KIND: Record<TaxonKindPath, "museum" | "movement" | "subject" | "genre"> = {
  museums: "museum", movements: "movement", subjects: "subject", genres: "genre",
};

/** A browse page is indexable only with enough works to be useful (thin-page guard). */
export const MIN_TAXON_WORKS = 3;
/** Per browse axis. Movements need ≥ 20 published works (owner, 2026-10-04); others keep the thin-page guard of 3. */
export const MIN_WORKS_BY_KIND: Record<"museum" | "movement" | "subject" | "genre", number> = { museum: 3, movement: 20, subject: 3, genre: 3 };
export const minWorks = (kind: string) => MIN_WORKS_BY_KIND[kind as keyof typeof MIN_WORKS_BY_KIND] ?? MIN_TAXON_WORKS;
/** SQL fragment: taxa row `alias` has enough published works for its kind. */
export const TAXON_VISIBLE_SQL = (alias = "") => `${alias}artwork_count >= CASE ${alias}kind WHEN 'movement' THEN ${MIN_WORKS_BY_KIND.movement} ELSE ${MIN_TAXON_WORKS} END`;
/** An artist appears publicly only with at least this many published works (owner, 2026-10-04). */
export const MIN_ARTIST_WORKS = 10;
export const PAGE_SIZE = 48;
